import { cn } from '@/app/utils/cn';
import CarouselWrapper from './CarouselWrapper';

const getColumnClass = (limit) => {
    const numericLimit = parseInt(limit, 10);

    if (isNaN(numericLimit) || numericLimit <= 0) {
        return 'grid-cols-cards sm:grid-cols-cards-lg';
    }

    const gridColumnMap = {
        1: 'grid-cols-1',
        2: 'sm:grid-cols-2',
        3: 'md:grid-cols-3',
        4: 'md:grid-cols-4'
    };
    return gridColumnMap[numericLimit] || 'grid-cols-cards sm:grid-cols-cards-lg';
};

const getFirstRowSize = (limit, deviceType = 'desktop') => {
    const numericLimit = parseInt(limit, 10);

    if (isNaN(numericLimit) || numericLimit <= 0) {
        return 1;
    }

    if (numericLimit === 5) return 1;
    if (numericLimit === 6) return 2;
    if (numericLimit === 7) return 3;

    if (deviceType === 'mobile') {
        return 1;
    } else if (deviceType === 'tablet-2-columns') {
        const tablet2ColRowSizeMap = {
            1: 1,
            2: 2, // sm:grid-cols-2 applies on tablet-2-columns
            3: 2, // Limited to 2 columns on tablet-2-columns
            4: 2 // Limited to 2 columns on tablet-2-columns
        };
        return tablet2ColRowSizeMap[numericLimit] || Math.min(numericLimit, 2);
    } else if (deviceType === 'tablet-3-columns') {
        const tablet3ColRowSizeMap = {
            1: 1,
            2: 2, // sm:grid-cols-2 applies on tablet-3-columns
            3: 3, // md:grid-cols-3 applies on tablet-3-columns
            4: 3 // Limited to 3 columns on tablet-3-columns
        };
        return tablet3ColRowSizeMap[numericLimit] || Math.min(numericLimit, 3);
    } else {
        const firstRowSizeMap = {
            1: 1,
            2: 2,
            3: 3,
            4: 4
        };
        return firstRowSizeMap[numericLimit] || Math.min(numericLimit, 4);
    }
};

const getFlexItemWidth = () => {
    return 'w-full md:w-[calc(25%-1.125rem)]';
};

function ListingContentlets({
    maxLimit,
    showAuthor,
    contentType,
    mosaicStyle,
    Card,
    contentlets,
    showTopMargin,
    centerBlogs,
    deviceType = 'desktop',
    autoPriority = false,
    showInSingleRow = false,
    isAllPostsSection = false,
    isShowInList = false
}) {
    const numericMaxLimit = parseInt(maxLimit, 10);

    const safeContentlets = Array.isArray(contentlets) ? contentlets : [];

    if (!safeContentlets || safeContentlets.length === 0) {
        return null;
    }

    const renderContent = () => {
        if (contentType === 'Announcement') {
            return (
                <ul className={cn('mx-auto', showTopMargin && 'md:!mt-12')}>
                    {safeContentlets.map((contentlet, i) => (
                        <li
                            className="card-hover-pop relative mb-6 break-inside-avoid rounded-lg shadow-md"
                            key={contentlet.title + i}>
                            <Card
                                horizontalCard={false}
                                showAuthor={showAuthor}
                                contentlet={contentlet}
                                priority={i === 0}
                            />
                        </li>
                    ))}
                </ul>
            );
        }

        if (mosaicStyle) {
            const firstRowSize = getFirstRowSize(numericMaxLimit, deviceType);

            return (
                <div
                    className={cn(
                        'columns-1 gap-6 md:columns-2 lg:columns-3 xl:columns-4',
                        showTopMargin && '!mt-12'
                    )}>
                    {safeContentlets.map((contentlet, i) => {
                        const isInFirstRow = i < firstRowSize;
                        return (
                            <div
                                className="card-hover-pop relative mb-6 break-inside-avoid rounded-lg shadow-md"
                                key={contentlet.title + i}>
                                <Card
                                    horizontalCard={false}
                                    showAuthor={showAuthor}
                                    contentlet={contentlet}
                                    priority={autoPriority && isInFirstRow}
                                />
                            </div>
                        );
                    })}
                </div>
            );
        }

        // Use carousel for category sections, but not for "All Posts" section
        if (showInSingleRow && !isAllPostsSection) {
            const MAX_ITEMS = 5;
            const carouselContentlets = safeContentlets.slice(0, MAX_ITEMS);

            return (
                <CarouselWrapper totalItems={carouselContentlets.length}>
                    {(cardWidthCalc) =>
                        carouselContentlets.map((contentlet, i) => (
                            <li
                                key={contentlet.title + i}
                                className="card-hover-pop relative shrink-0 rounded-lg shadow-md"
                                style={{
                                    width: `calc(${cardWidthCalc})`
                                }}>
                                <Card
                                    horizontalCard={false}
                                    showAuthor={showAuthor}
                                    contentlet={contentlet}
                                    priority={i === 0}
                                />
                            </li>
                        ))
                    }
                </CarouselWrapper>
            );
        }

        if (numericMaxLimit === 5 || numericMaxLimit === 6 || numericMaxLimit === 7) {
            const specialConfig = {
                5: { firstRowSize: 1, firstRowCols: 'md:grid-cols-1' },
                6: { firstRowSize: 2, firstRowCols: 'md:grid-cols-2' },
                7: { firstRowSize: 3, firstRowCols: 'md:grid-cols-3' }
            };

            const config = specialConfig[numericMaxLimit];
            const firstRow = safeContentlets.slice(0, config.firstRowSize);
            const rest = safeContentlets.slice(config.firstRowSize, numericMaxLimit);
            const secondRowCols = 'md:grid-cols-4';

            return (
                <>
                    <ul
                        className={cn(
                            'grid w-full gap-x-6 gap-y-12',
                            config.firstRowCols,
                            showTopMargin && '!mt-12'
                        )}>
                        {firstRow.map((contentlet, i) => (
                            <li
                                className="card-hover-pop relative mb-6 break-inside-avoid rounded-lg shadow-md"
                                key={contentlet.title + i}>
                                <Card
                                    horizontalCard={!!(numericMaxLimit === 5)}
                                    showAuthor={showAuthor}
                                    contentlet={contentlet}
                                    priority={true}
                                />
                            </li>
                        ))}
                    </ul>
                    <ul className={cn('mt-6 grid w-full gap-x-6 gap-y-12', secondRowCols)}>
                        {rest.map((contentlet, i) => (
                            <li
                                className="card-hover-pop relative mb-6 break-inside-avoid rounded-lg shadow-md"
                                key={contentlet.title + (i + 2)}>
                                <Card
                                    horizontalCard={false}
                                    showAuthor={false}
                                    contentlet={contentlet}
                                />
                            </li>
                        ))}
                    </ul>
                </>
            );
        }

        const shouldUseFlex = centerBlogs && safeContentlets.length < 4;
        const firstRowSize = getFirstRowSize(numericMaxLimit, deviceType);

        return (
            <ul
                className={
                    isShowInList
                        ? ''
                        : cn(
                              'w-full gap-x-6 gap-y-12',
                              showTopMargin && '!mt-12',
                              shouldUseFlex
                                  ? 'flex flex-wrap justify-center'
                                  : cn('grid', getColumnClass(numericMaxLimit))
                          )
                }>
                {safeContentlets.map((contentlet, i) => {
                    const isInFirstRow = i < firstRowSize;
                    return (
                        <li
                            className={cn(
                                'card-hover-pop relative mb-6 break-inside-avoid rounded-lg shadow-md',
                                shouldUseFlex && getFlexItemWidth()
                            )}
                            key={contentlet.title + i}>
                            <Card
                                horizontalCard={false}
                                showAuthor={showAuthor}
                                contentlet={contentlet}
                                priority={autoPriority && isInFirstRow}
                            />
                        </li>
                    );
                })}
            </ul>
        );
    };

    return renderContent();
}

export default ListingContentlets;
